// src/native/compliance/consentConfig.js
//
// WS-7 COMPLIANCE. Configuration for the App Store Guideline 5.1.2(i) consent
// surface (third-party AI data sharing) and the Guideline 5.1.4(b) minors
// surface.
//
// Nothing in this file has side effects and nothing here imports React, so it
// is safe to import from a store, a hook or a screen.

import { isNative, nativePlatform } from '@/lib/platform';

// ---------------------------------------------------------------------------
// THE GATE SCOPE SWITCH. FOUNDER DECISION, DO NOT FLIP WITHOUT ONE.
// ---------------------------------------------------------------------------
//
// 'both' (current setting, decided by the founder on 2026-08-11):
//   the consent gate runs on native iOS AND on the web. The reasoning is the
//   GDPR position rather than the App Store rule: the product serves minors in
//   the EU, and Lithuania, the primary market, sets the digital consent age at
//   14. Note that this puts a blocking screen inside a live funnel that is
//   converting real paying customers, which is why AiConsentGate exempts the
//   marketing and checkout routes and paints from a synchronous read.
//
// 'native-only':
//   the gate runs in the native iOS app only. Every browser, including the
//   installed PWA, behaves exactly as it does today. This is what App Store
//   Guideline 5.1.2(i) requires on its own, and it is the setting to fall back
//   to if the gate is ever shown to cost conversion.
//
// This is the ONLY switch. Every other module asks isAiConsentRequiredHere().
//
// BOTH VALUES ARE SAFE TO SHIP, and the retiming to first-AI-use is what makes
// that true. Flipping this to 'native-only' collapses the whole feature to a
// no-op on the web in one hop, with nothing else to change:
//
//   isAiSharingAllowed()      peekConsentSync() reports required:false, so it
//                             returns true before reading any storage. Every
//                             guard added at every call site becomes a single
//                             comparison that always passes.
//   assertAiSharingAllowed()  defers to the above, so it never throws.
//   AiConsentGate             returns null before calling a hook.
//   AiConsentSettingsRow      returns null.
//
// Worth knowing when choosing: CONSENT_LOCAL_KEY is a NEW key, so on the first
// load after deploy every existing paying customer reads as not-consented under
// 'both'. That is survivable only because the gate is now triggered by AI use
// rather than by launch: nobody is blocked on open, nobody in the funnel sees
// anything, and the ask lands on the first tap of an AI feature.
export const AI_CONSENT_GATE_SCOPE = 'both';

/** True when the consent gate applies on this runtime. */
export function isAiConsentRequiredHere() {
  if (AI_CONSENT_GATE_SCOPE === 'native-only') return isNative();
  return true;
}

// ---------------------------------------------------------------------------
// Versioning
// ---------------------------------------------------------------------------
//
// Bump CONSENT_VERSION whenever AI_PROCESSORS changes in a way a reasonable
// person would want to know about: a new company, a new category of data, or a
// new purpose. A stored consent with an older version is treated as not given,
// so the user is asked again. Removing a processor is also a bump, because the
// screen they agreed to no longer matches reality.
export const CONSENT_VERSION = 1;

// The minors acknowledgement is versioned separately. It changes far less often
// than the processor list, and re-asking a parent who has already answered is
// friction with no benefit.
export const MINORS_ACK_VERSION = 1;

// ---------------------------------------------------------------------------
// The processors
// ---------------------------------------------------------------------------
//
// Guideline 5.1.2(i), updated 2025-11-13: "You must clearly disclose where
// personal data will be shared with third parties, including with third-party
// AI, and obtain explicit permission before doing so." A link to the privacy
// policy is explicitly not enough, so every company below is named on screen,
// with what it receives and why.
//
// Company names are proper nouns and are deliberately NOT translated.
// `receivesKey` and `purposeKey` resolve through translations.js.
export const AI_PROCESSORS = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    receivesKey: 'native.consent.proc.anthropic.receives',
    purposeKey: 'native.consent.proc.anthropic.purpose',
  },
  {
    id: 'google',
    name: 'Google',
    receivesKey: 'native.consent.proc.google.receives',
    purposeKey: 'native.consent.proc.google.purpose',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    receivesKey: 'native.consent.proc.openai.receives',
    purposeKey: 'native.consent.proc.openai.purpose',
  },
  {
    id: 'deepgram',
    name: 'Deepgram',
    receivesKey: 'native.consent.proc.deepgram.receives',
    purposeKey: 'native.consent.proc.deepgram.purpose',
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    receivesKey: 'native.consent.proc.elevenlabs.receives',
    purposeKey: 'native.consent.proc.elevenlabs.purpose',
  },
];

/** Stable list of processor ids, stored alongside the consent record. */
export const AI_PROCESSOR_IDS = AI_PROCESSORS.map((p) => p.id);

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

/** localStorage. Works everywhere, including before sign-in. */
export const CONSENT_LOCAL_KEY = 'ttt_ai_consent_v1';
export const MINORS_LOCAL_KEY = 'ttt_minors_ack_v1';

/**
 * @capacitor/preferences. Native only. Survives a webview data clear, which
 * localStorage does not, so this is the durable copy on iOS.
 */
export const CONSENT_PREFS_KEY = 'ttt_ai_consent';
export const MINORS_PREFS_KEY = 'ttt_minors_ack';

/**
 * Supabase auth user_metadata. Survives reinstall and follows the account to a
 * new device. Mirrors how onboarding_completed is already stored
 * (AuthContext.markOnboardingComplete).
 */
export const CONSENT_METADATA_KEY = 'ai_consent';
export const MINORS_METADATA_KEY = 'minors_ack';

// ---------------------------------------------------------------------------
// Published contact, Guideline 1.2
// ---------------------------------------------------------------------------

/** Already the app's published address, used across the site and emails. */
export const SUPPORT_EMAIL = 'info@timetotalk.lt';

/** In-app route. Bundled React page, no Safari hop (see spec section 7.1). */
export const PRIVACY_ROUTE = '/privacy';

// ---------------------------------------------------------------------------
// Minors, Guideline 5.1.4(b)
// ---------------------------------------------------------------------------
//
// Ages are shown to the user as plain sentences, not as a legal citation.
// The numbers behind them:
//   13  COPPA, United States
//   14  GDPR Article 8 as implemented in Lithuania, the primary market
//   16  GDPR Article 8 default, used by most other member states
// Article 8 lets a member state lower the default to no less than 13, so the
// range 13 to 16 covers every locale this app ships in.
//
// The affirmation is folded into the single "I agree" tap rather than made its
// own step. The gate now sits in a live paying funnel, and a two screen wall
// costs conversion for no extra legal weight: an explicit affirmative act that
// carries the affirmation in the sentence above the button is exactly what
// Article 8 and 5.1.4(b) ask for.
export const MINORS_ROLE_AFFIRMED = 'adult_or_guardian_affirmed';
export const MINORS_ROLE_UNDER_AGE = 'under_age_deferred';

// ---------------------------------------------------------------------------
// Report reasons, Guideline 1.2
// ---------------------------------------------------------------------------

export const REPORT_REASONS = [
  { id: 'unkind', labelKey: 'native.report.reason.unkind' },
  { id: 'unsafe', labelKey: 'native.report.reason.unsafe' },
  { id: 'wrong', labelKey: 'native.report.reason.wrong' },
  { id: 'language', labelKey: 'native.report.reason.language' },
  { id: 'other', labelKey: 'native.report.reason.other' },
];

/** Reasons that make the calm emergency line worth showing. */
export const REPORT_REASONS_WITH_SAFETY_NOTE = new Set(['unsafe']);

/** Free text cap. Keeps a runaway paste out of the database. */
export const REPORT_NOTE_MAX = 1000;

/** How much of the reported reply we store. Enough to judge it, not a transcript. */
export const REPORT_TEXT_MAX = 4000;

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Local ISO 8601 timestamp with a real offset, for example
 * 2026-08-11T14:32:05+03:00.
 *
 * Deliberately not toISOString(). This codebase has already shipped one bug
 * where a UTC date reset a streak a few hours early for users east of Greenwich,
 * and a consent record that claims the wrong day is worse than most bugs.
 */
export function localIsoTimestamp(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

/** 'ios' | 'android' | 'web'. Stamped onto consent and report records. */
export function currentPlatformTag() {
  return nativePlatform();
}
